import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, PawPrint, Heart, BookOpen, Sparkles } from "lucide-react";
import { getArticles, getCategories, CATEGORY_STYLES } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";
import Newsletter from "@/components/Newsletter";

const CATEGORY_IMAGES = {
  dogs: "https://images.pexels.com/photos/8394203/pexels-photo-8394203.jpeg",
  cats: "https://images.pexels.com/photos/30555071/pexels-photo-30555071.jpeg",
  birds: "https://images.pexels.com/photos/1893158/pexels-photo-1893158.jpeg",
  "small-pets":
    "https://images.unsplash.com/photo-1585110396000-c9ffd4e4b308?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMHBldCUyMHJhYmJpdCUyMGJ1bm55JTIwbmF0dXJlfGVufDB8fHx8MTc4MzUwMjk2MXww&ixlib=rb-4.1.0&q=85",
};

export default function Home() {
  const { data: featured = [] } = useQuery({
    queryKey: ["articles", "featured"],
    queryFn: () => getArticles({ featured: true }),
  });
  const { data: latest = [] } = useQuery({
    queryKey: ["articles", "latest"],
    queryFn: () => getArticles(),
  });
  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  const heroArticle = featured[0];
  const recent = latest.slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5 animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#DDA77B]/25 text-[#B07C4F] px-4 py-1.5 text-xs font-sans font-medium uppercase tracking-wider">
            <PawPrint size={14} strokeWidth={2} /> Pet care, with heart
          </span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#2C3529] mt-6 leading-[1.05]">
            Caring for the ones who love you back
          </h1>
          <p className="font-sans text-lg text-[#576054] leading-relaxed mt-6 max-w-md">
            Warm, honest guides and stories to help you understand, nurture,
            and celebrate the animals who share your home.
          </p>
          <div className="flex flex-wrap gap-4 mt-9">
            <Link
              to="/blog"
              data-testid="hero-explore-button"
              className="rounded-full bg-[#D96B43] text-white px-8 py-3.5 font-sans font-medium hover:-translate-y-1 hover:bg-[#C25D3A] transition-all duration-300 shadow-sm flex items-center gap-2"
            >
              Explore the Journal <ArrowRight size={18} />
            </Link>
            <Link
              to="/about"
              data-testid="hero-about-button"
              className="rounded-full border-2 border-[#2C3529] text-[#2C3529] px-8 py-3.5 font-sans font-medium hover:bg-[#2C3529] hover:text-white transition-all duration-300"
            >
              Our Story
            </Link>
          </div>
        </div>
        <div className="md:col-span-7 animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative">
            <div className="overflow-hidden rounded-[2.5rem] aspect-[5/4]">
              <img
                src="https://images.pexels.com/photos/9815581/pexels-photo-9815581.jpeg"
                alt="A woman lovingly holding her dog"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-2 sm:left-6 bg-[#FAF9F6] rounded-2xl shadow-lg px-6 py-4 flex items-center gap-3 border border-[#EAE4D9]">
              <span className="grid place-items-center h-11 w-11 rounded-full bg-[#839788]/20 text-[#5C6B5F]">
                <Heart size={20} strokeWidth={1.75} />
              </span>
              <div>
                <p className="font-serif text-2xl text-[#2C3529] leading-none">12k+</p>
                <p className="font-sans text-xs uppercase tracking-wider text-[#8a9184] mt-1">
                  Happy pet parents
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 sm:grid-cols-3 gap-8">
        {[
          { icon: BookOpen, title: "Expert-written", text: "Guidance from behaviourists, vets, and lifelong animal lovers." },
          { icon: Heart, title: "Gentle & practical", text: "Kind, realistic advice you can actually use — no jargon." },
          { icon: Sparkles, title: "For every companion", text: "Dogs, cats, birds and small pets, each with their own care." },
        ].map((f, i) => (
          <div key={i} className="flex gap-4 items-start">
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[#D96B43]/12 text-[#D96B43] shrink-0">
              <f.icon size={22} strokeWidth={1.5} />
            </span>
            <div>
              <h3 className="font-serif text-2xl text-[#2C3529]">{f.title}</h3>
              <p className="font-sans text-[#576054] mt-1 leading-relaxed">{f.text}</p>
            </div>
          </div>
        ))}
      </section>

      {/* FEATURED */}
      {heroArticle && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2C3529]">
              Featured reading
            </h2>
            <Link to="/blog" className="hidden sm:flex items-center gap-2 font-sans text-sm uppercase tracking-wider text-[#D96B43] hover:gap-3 transition-all">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <Link
            to={`/blog/${heroArticle.slug}`}
            data-testid="featured-article"
            className="group grid grid-cols-1 md:grid-cols-12 gap-8 items-center"
          >
            <div className="md:col-span-7 overflow-hidden rounded-[2.5rem] aspect-[16/10]">
              <img
                src={heroArticle.image}
                alt={heroArticle.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="md:col-span-5">
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-sans font-medium uppercase tracking-wider ${CATEGORY_STYLES[heroArticle.category]}`}>
                Featured
              </span>
              <h3 className="font-serif text-3xl sm:text-4xl text-[#2C3529] mt-4 leading-snug group-hover:text-[#D96B43] transition-colors">
                {heroArticle.title}
              </h3>
              <p className="font-sans text-[#576054] leading-relaxed mt-4">
                {heroArticle.excerpt}
              </p>
              <span className="inline-flex items-center gap-2 mt-6 font-sans text-sm uppercase tracking-wider text-[#2C3529] group-hover:text-[#D96B43] transition-colors">
                Read the story <ArrowRight size={16} />
              </span>
            </div>
          </Link>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2C3529] mb-8">
          Explore by companion
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/blog?category=${c.slug}`}
              data-testid={`category-tile-${c.slug}`}
              className="group relative overflow-hidden rounded-[2rem] aspect-[3/4] animate-fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}
            >
              <img
                src={CATEGORY_IMAGES[c.slug]}
                alt={c.name}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C3529]/85 via-[#2C3529]/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <h3 className="font-serif text-2xl text-white">{c.name}</h3>
                <p className="font-sans text-xs text-white/80 uppercase tracking-wider mt-1 flex items-center gap-1">
                  Read more <ArrowRight size={13} />
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* LATEST */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2C3529]">
            Fresh from the journal
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {recent.map((a, i) => (
            <ArticleCard key={a.id} article={a} index={i} />
          ))}
        </div>
      </section>

      <Newsletter />
    </div>
  );
}
